import { Controller } from 'react-hook-form';
import type { Control, FieldPath, FieldValues } from 'react-hook-form';
import { Field, FieldError, FieldLabel } from '@/shared/components/ui/field';

interface FormFieldProps<T extends FieldValues> {
  control: Control<T>;
  name: FieldPath<T>;
  label: string;
  disabled?: boolean;
  /**
   * Hide the visible label. This primitive does not render the control, so giving it
   * an accessible name is the caller's job — `FormFieldSelect` emits `aria-label`.
   * Passing this without naming the control leaves it unnamed.
   */
  hideLabel?: boolean;
  render: (props: {
    field: React.InputHTMLAttributes<HTMLElement> & {
      value: unknown;
      onChange: (...event: unknown[]) => void;
      onBlur: () => void;
      // ref: React.Ref<HTMLElement | undefined>;
    };
    invalid: boolean;
    disabled: boolean;
  }) => React.ReactNode;
}

export function FormField<T extends FieldValues>({
  control,
  name,
  label,
  disabled = false,
  hideLabel = false,
  render,
}: FormFieldProps<T>) {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => (
        <Field data-invalid={fieldState.invalid}>
          {!hideLabel && <FieldLabel htmlFor={name}>{label}</FieldLabel>}
          {render({
            field,
            invalid: fieldState.invalid,
            disabled,
          })}
          {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
        </Field>
      )}
    />
  );
}
